import { useState } from 'react';
import type { FactorKey, Meta, NotificationPrefs, OrgPriority, Profile, Project, RolePreset } from '../types';

interface Props {
  meta: Meta | null;
  profile: Profile | null;
  projects: Project[];
  priorities: OrgPriority[];
  notifications: NotificationPrefs | null;
  onProfileChange: (patch: Record<string, unknown>) => void;
  onCreateProject: (name: string, orgPriorityId: string | null) => void;
  onUpdateProject: (id: string, patch: Record<string, unknown>) => void;
  onDeleteProject: (id: string) => void;
  onCreatePriority: (name: string, weight: number) => void;
  onUpdatePriority: (id: string, patch: Record<string, unknown>) => void;
  onDeletePriority: (id: string) => void;
  onNotificationsChange: (patch: Partial<NotificationPrefs>) => void;
}

export function SettingsPanel({
  meta,
  profile,
  projects,
  priorities,
  notifications,
  onProfileChange,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onCreatePriority,
  onUpdatePriority,
  onDeletePriority,
  onNotificationsChange,
}: Props) {
  const [projectName, setProjectName] = useState('');
  const [projectPriority, setProjectPriority] = useState('');
  const [priorityName, setPriorityName] = useState('');
  const [priorityWeight, setPriorityWeight] = useState(2);

  if (!profile || !meta) return <div className="empty">Loading…</div>;

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2>What matters in your role</h2>
        </div>
        <div className="card-body form-grid">
          <div className="form-row">
            <label>Role</label>
            <div className="role-picker">
              {meta.roles
                .filter((r) => r.id !== 'custom')
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="role-option"
                    aria-pressed={profile.role === r.id}
                    onClick={() => onProfileChange({ role: r.id })}
                  >
                    {r.label}
                  </button>
                ))}
            </div>
            <span className="help">
              {profile.role === 'custom'
                ? 'You have adjusted the weights by hand. Pick a role to load its defaults again.'
                : 'A starting point. Every slider below stays editable.'}
            </span>
          </div>

          <div className="section">
            <h4>Weights</h4>
            <div className="weights">
              {meta.factors.map((f) => (
                <div className="weight-row" key={f.id}>
                  <label htmlFor={`w-${f.id}`}>{f.label}</label>
                  <input
                    id={`w-${f.id}`}
                    type="range"
                    min={0}
                    max={5}
                    step={0.25}
                    value={profile.weights[f.id as FactorKey] ?? 0}
                    onChange={(e) =>
                      onProfileChange({
                        weights: { ...profile.weights, [f.id]: Number(e.target.value) },
                      })
                    }
                  />
                  <span className="val">{(profile.weights[f.id as FactorKey] ?? 0).toFixed(2)}</span>
                  <span className="blurb">{f.blurb}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="section">
            <h4>Age pressure</h4>
            <div className="weight-row">
              <label htmlFor="age-days">Reaches full weight after</label>
              <input
                id="age-days"
                type="range"
                min={3}
                max={120}
                step={1}
                value={profile.agePressureDays}
                onChange={(e) => onProfileChange({ agePressureDays: Number(e.target.value) })}
              />
              <span className="val">{profile.agePressureDays}d</span>
              <span className="blurb">
                How long something has to sit before its age counts for the full Age weight above.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Organizational priorities</h2>
        </div>
        <div className="card-body">
          <span className="help">
            What your company or team is pushing on right now. Projects tagged with a priority score higher on
            alignment; the weight sets how much higher.
          </span>
          <div className="mini-list" style={{ marginTop: 12 }}>
            {priorities.map((p) => (
              <div className="mini-row" key={p.id}>
                <div className="grow">
                  <div className="name">{p.name}</div>
                  <div className="sub">
                    {p.projectCount} project{p.projectCount === 1 ? '' : 's'}
                    {p.detail ? ` · ${p.detail}` : ''}
                  </div>
                </div>
                <label className="checkbox" title="Inactive priorities stop counting toward alignment">
                  <input
                    type="checkbox"
                    checked={p.active}
                    onChange={(e) => onUpdatePriority(p.id, { active: e.target.checked })}
                  />
                  Active
                </label>
                <input
                  type="range"
                  min={0}
                  max={3}
                  step={0.5}
                  value={p.weight}
                  style={{ width: 100 }}
                  onChange={(e) => onUpdatePriority(p.id, { weight: Number(e.target.value) })}
                />
                <span className="val">{p.weight.toFixed(1)}</span>
                <button className="btn sm danger" onClick={() => onDeletePriority(p.id)}>
                  Remove
                </button>
              </div>
            ))}
            {!priorities.length && <span className="help">None yet.</span>}
          </div>
          <div className="inline-form">
            <input
              type="text"
              placeholder="e.g. Q4 enterprise readiness"
              value={priorityName}
              onChange={(e) => setPriorityName(e.target.value)}
            />
            <input
              type="range"
              min={0}
              max={3}
              step={0.5}
              value={priorityWeight}
              onChange={(e) => setPriorityWeight(Number(e.target.value))}
              style={{ width: 120 }}
            />
            <span className="val">{priorityWeight.toFixed(1)}</span>
            <button
              className="btn"
              disabled={!priorityName.trim()}
              onClick={() => {
                onCreatePriority(priorityName.trim(), priorityWeight);
                setPriorityName('');
              }}
            >
              Add priority
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Projects</h2>
        </div>
        <div className="card-body">
          <span className="help">Group related tasks across systems, and tie a group to an org priority.</span>
          <div className="mini-list" style={{ marginTop: 12 }}>
            {projects.map((p) => (
              <div className="mini-row" key={p.id}>
                <div className="grow">
                  <div className="name">{p.name}</div>
                  <div className="sub">
                    {p.taskCount} task{p.taskCount === 1 ? '' : 's'}
                  </div>
                </div>
                <select
                  value={p.orgPriorityId ?? ''}
                  onChange={(e) => onUpdateProject(p.id, { orgPriorityId: e.target.value || null })}
                >
                  <option value="">No priority</option>
                  {priorities.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.name}
                    </option>
                  ))}
                </select>
                <button className="btn sm danger" onClick={() => onDeleteProject(p.id)}>
                  Remove
                </button>
              </div>
            ))}
            {!projects.length && <span className="help">None yet.</span>}
          </div>
          <div className="inline-form">
            <input
              type="text"
              placeholder="e.g. SSO rollout"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
            />
            <select value={projectPriority} onChange={(e) => setProjectPriority(e.target.value)}>
              <option value="">No priority</option>
              {priorities.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.name}
                </option>
              ))}
            </select>
            <button
              className="btn"
              disabled={!projectName.trim()}
              onClick={() => {
                onCreateProject(projectName.trim(), projectPriority || null);
                setProjectName('');
                setProjectPriority('');
              }}
            >
              Add project
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Notifications</h2>
        </div>
        <div className="card-body form-grid">
          {!notifications ? (
            <span className="help">Loading…</span>
          ) : (
            <>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifications.enabled}
                  onChange={(e) => onNotificationsChange({ enabled: e.target.checked })}
                />
                Email me about my tasks
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  disabled={!notifications.enabled}
                  checked={notifications.dueSoon}
                  onChange={(e) => onNotificationsChange({ dueSoon: e.target.checked })}
                />
                Remind me before a task is due
              </label>
              <div className="form-row">
                <label>How far ahead</label>
                <input
                  type="number"
                  min={1}
                  max={240}
                  disabled={!notifications.enabled || !notifications.dueSoon}
                  value={notifications.dueSoonHours}
                  onChange={(e) => onNotificationsChange({ dueSoonHours: Math.max(1, Math.min(240, Number(e.target.value) || 1)) })}
                  style={{ width: 80 }}
                />
                <span className="help">hours before the due date</span>
              </div>
              <label className="checkbox">
                <input
                  type="checkbox"
                  disabled={!notifications.enabled}
                  checked={notifications.overdue}
                  onChange={(e) => onNotificationsChange({ overdue: e.target.checked })}
                />
                Tell me when a task goes overdue
              </label>
              <span className="help">Each task sends at most one due-soon and one overdue email — nothing repeats.</span>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export type { RolePreset };
